import { type FormEvent, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@/components/icon/Icon";
import { api, ApiError } from "@/lib/api";
import { validateInquiryForm, VIEW_PASSWORD_MIN, type InquiryFormErrors } from "@/lib/inquiry-form";
import { formatWon } from "@/lib/quote";
import { track, trackInquirySessionId } from "@/lib/track";
import type { QuoteSnapshotDTO } from "@/types/inquiry";

interface InquiryLocationState {
  quote?: QuoteSnapshotDTO;
  notes?: string;
}

type SubmitState = "idle" | "submitting" | "success" | "error";

function QuoteSummaryCard({ quote }: { quote: QuoteSnapshotDTO }) {
  // 라벨은 스냅샷에 얼려 담겨 있어요 — 단가 항목이 나중에 바뀌어도 이 요약은 그대로예요.
  const optionLabels = quote.options.map((o) => o.label).join(", ");

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-background-elevated p-6 shadow-normal">
      <span className="wds-label-1 text-label-assistive">견적 요약</span>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 wds-body-2">
        <dt className="text-label-assistive">유형</dt>
        <dd className="text-label-normal">{quote.type_label ?? quote.type}</dd>
        <dt className="text-label-assistive">페이지 수</dt>
        <dd className="tabular-nums text-label-normal">{quote.pages}</dd>
        <dt className="text-label-assistive">선택 옵션</dt>
        <dd className="text-label-normal">{optionLabels || "없음"}</dd>
        <dt className="text-label-assistive">월 지원</dt>
        <dd className="text-label-normal">{quote.support_label ?? quote.support}</dd>
      </dl>
      <div className="flex flex-col gap-1 border-line-alternative border-t pt-4">
        <span className="wds-caption-1 text-label-assistive">예상 할인가</span>
        <div className="flex items-baseline gap-2">
          <span className="wds-body-2 text-label-assistive line-through tabular-nums">{formatWon(quote.standard)}</span>
          <span className="wds-title-2 font-bold text-primary tabular-nums">{formatWon(quote.discounted)}</span>
        </div>
        <span className="wds-caption-1 text-label-assistive">
          월 유지보수 <span className="tabular-nums">{formatWon(quote.monthly)}</span>
          {/* 롤링 배포 중 구 api 응답엔 이 필드가 없어요. */}
          {(quote.monthly_free_months ?? 0) > 0 && ` (첫 ${quote.monthly_free_months}개월 무료)`}
        </span>
      </div>
    </div>
  );
}

export function InquiryPage() {
  const location = useLocation();
  const state = (location.state as InquiryLocationState | null) ?? null;
  const quote = state?.quote;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState(state?.notes ?? "");
  const [viewPassword, setViewPassword] = useState("");
  const [errors, setErrors] = useState<InquiryFormErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    track("inquiry_opened");
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const nextErrors = validateInquiryForm({ name, email, message, viewPassword });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitState("submitting");
    try {
      await api.post("/inquiry", {
        contact: { name: name.trim(), email: email.trim(), phone: phone.trim() || undefined },
        message: message.trim(),
        quote: quote ?? undefined,
        session_id: trackInquirySessionId() ?? undefined,
        view_password: viewPassword.trim() || undefined,
      });
      setSubmitState("success");
    } catch (err) {
      setErrorMessage(err instanceof ApiError ? err.message : "문의 전송에 실패했어요. 잠시 후 다시 시도해 주세요.");
      setSubmitState("error");
    }
  };

  return (
    <section className="mx-auto flex min-h-screen max-w-layout flex-col gap-8 px-6 py-16">
      <Link to="/" className="wds-label-1 inline-flex w-fit items-center gap-1.5 text-label-neutral hover:text-primary">
        <Icon name="arrowLeft" size={16} />
        홈으로
      </Link>

      {submitState === "success" ? (
        <div className="flex flex-col items-center gap-4 rounded-lg bg-background-elevated py-24 text-center shadow-normal">
          <Icon name="circleCheckFill" size={40} className="text-primary" />
          <h1 className="wds-title-1 text-label-strong">문의가 접수됐어요</h1>
          <p className="wds-body-1-reading text-label-neutral">
            남겨주신 연락처로 담당자가 24시간 이내에 답변드릴게요. 감사합니다.
          </p>
          {viewPassword.trim() && (
            <p className="wds-body-2 text-label-neutral">
              정하신 조회 비밀번호로{" "}
              <Link to="/inquiries" className="font-semibold text-primary underline underline-offset-4">
                문의 현황
              </Link>
              에서 진행 상황을 확인하실 수 있어요.
            </p>
          )}
          <Link
            to="/"
            className="mt-2 inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong"
          >
            홈으로 돌아가기
          </Link>
        </div>
      ) : (
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          {quote && <QuoteSummaryCard quote={quote} />}

          <form
            onSubmit={handleSubmit}
            className={`flex flex-col gap-6 ${quote ? "" : "lg:col-span-2 lg:max-w-xl"}`}
            noValidate
          >
            <div className="flex flex-col gap-2">
              <h1 className="wds-title-1 text-label-strong">{quote ? "견적 문의하기" : "문의하기"}</h1>
              <p className="wds-body-2 text-label-neutral">
                담당자 확인 후 남겨주신 연락처로 24시간 이내에 답변드려요.
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="inquiry-name" className="wds-label-1 text-label-strong">
                이름
              </label>
              <input
                id="inquiry-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "inquiry-name-error" : undefined}
                className="wds-body-2 rounded-md border border-line-neutral bg-background-normal px-4 py-2.5 text-label-normal"
              />
              {errors.name && (
                <p id="inquiry-name-error" className="wds-caption-1 text-status-negative">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="inquiry-email" className="wds-label-1 text-label-strong">
                이메일
              </label>
              <input
                id="inquiry-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "inquiry-email-error" : undefined}
                className="wds-body-2 rounded-md border border-line-neutral bg-background-normal px-4 py-2.5 text-label-normal"
              />
              {errors.email && (
                <p id="inquiry-email-error" className="wds-caption-1 text-status-negative">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="inquiry-phone" className="wds-label-1 text-label-strong">
                연락처 <span className="text-label-assistive">(선택)</span>
              </label>
              <input
                id="inquiry-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="wds-body-2 rounded-md border border-line-neutral bg-background-normal px-4 py-2.5 text-label-normal"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="inquiry-message" className="wds-label-1 text-label-strong">
                문의 내용
              </label>
              <textarea
                id="inquiry-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "inquiry-message-error" : undefined}
                className="wds-body-2 rounded-md border border-line-neutral bg-background-normal px-4 py-3 text-label-normal"
              />
              {errors.message && (
                <p id="inquiry-message-error" className="wds-caption-1 text-status-negative">
                  {errors.message}
                </p>
              )}
            </div>

            {/* 선택 항목이라 리드폼 마찰을 늘리지 않아요 — 안 정하면 게시판에서 열람만 못 해요. */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="inquiry-view-password" className="wds-label-1 text-label-strong">
                조회 비밀번호 <span className="text-label-assistive">(선택)</span>
              </label>
              <input
                id="inquiry-view-password"
                type="password"
                value={viewPassword}
                onChange={(e) => setViewPassword(e.target.value)}
                autoComplete="new-password"
                aria-invalid={Boolean(errors.viewPassword)}
                aria-describedby="inquiry-view-password-hint"
                className="wds-body-2 h-11 rounded-md border border-line-neutral bg-background-normal px-4 text-label-normal"
              />
              <p id="inquiry-view-password-hint" className="wds-caption-1 text-label-assistive">
                정해두시면{" "}
                <Link to="/inquiries" className="font-medium text-primary underline underline-offset-4">
                  문의 현황
                </Link>
                에서 진행 상황을 직접 확인하실 수 있어요. {VIEW_PASSWORD_MIN}자 이상이면 돼요.
              </p>
              {errors.viewPassword && (
                <p className="wds-caption-1 text-status-negative">{errors.viewPassword}</p>
              )}
            </div>

            {submitState === "error" && (
              <output className="wds-body-2 rounded-md bg-background-alternative px-4 py-3 text-status-negative">
                {errorMessage}
              </output>
            )}

            <button
              type="submit"
              disabled={submitState === "submitting"}
              className="inline-flex h-11 w-fit items-center justify-center gap-2 rounded-md bg-primary px-6 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitState === "submitting" ? "전송 중…" : submitState === "error" ? "다시 시도" : "문의 보내기"}
              <Icon name="arrowRight" size={16} />
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

export default InquiryPage;
